"use client";

import { useRef, useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetFooter,
} from "@/components/ui/sheet";
import { t } from "i18next";
import AudioRecorderUI from "../audio-recorder";
import RecorderAudioPlayer from "../audio-recorder/recorder-audio-player";
import { useToast } from "@/hooks/use-toast";
import { useAuthStore } from "@/store/auth.store";
import getDuration from "@/lib/getDuration";
import { getBlobFromUrl } from "@/lib/getBlobFromUrl";
import { useVisitStore } from "@/store/visit.store";
import ConfirmDialog from "../ConfirmDialog";
import MicrophoneIcon from "@/assets/icons/mic.svg?react";
import { useRecordingUsageStore } from "@/store/recordingUsage.store";
import { SubscriptionOnlyButton } from "../SubscriptionOnlyButton/SubscriptionOnlyButton";
import { ValidationSuscriptionButton } from "@/common/enums/enums";
import { AlertMessage } from "../AlertMessage";
import { useAudioValidation } from "@/hooks/useAudioValidation";
import { UploadAudioButton } from "./UploadRecordButton";

export function MultiRecordingSidebar({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}): JSX.Element {
  const { toast } = useToast();
  const [showRecorder, setShowRecorder] = useState(false);
  const [isFileUpload, setIsFileUpload] = useState(false);
  const [audioFinished, setAudioFinished] = useState(false);
  const [recordedBlobUrl, setRecordedBlobUrl] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [showCancelConfirmation, setShowCancelConfirmation] = useState(false);
  const { fetchAndSetAvailableMinutes } = useRecordingUsageStore.getState();
  const { uploadMultiRecordingFile, loadingMultiRecordingFile, getFile, file } =
    useVisitStore();
  const { userData } = useAuthStore();
  const alreadyCompletedRef = useRef<boolean>(false);
  const { error, validateFile, resetError } = useAudioValidation();

  const handleRecordingComplete = (blobUrl: string): void => {
    if (alreadyCompletedRef.current) return;
    alreadyCompletedRef.current = true;

    setRecordedBlobUrl(blobUrl);
    setAudioFinished(true);
    if (userData) void fetchAndSetAvailableMinutes(userData.id);
  };

  const handleFileChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    const fileUploaded = event.target.files?.[0];
    if (fileUploaded && validateFile(fileUploaded)) {
      const fileUrl = URL.createObjectURL(fileUploaded);
      setRecordedBlobUrl(fileUrl);
      setAudioFinished(true);
      setIsFileUpload(true);
      if (userData) void fetchAndSetAvailableMinutes(userData.id);
    }
  };

  const handleSubmit = async (): Promise<void> => {
    if (!recordedBlobUrl) return;
    setSubmitting(true);
    try {
      const audioBlob = await getBlobFromUrl(recordedBlobUrl);
      const duration = await getDuration(audioBlob);
      if (userData?.id && file?.id) {
        await uploadMultiRecordingFile({
          file: audioBlob,
          fileId: file.id,
          userId: userData.id,
          duration,
        });
        toast({
          description: t(
            "visit.sidebar.multiRecording.messages.uploadSuccessful.description"
          ),
        });
        void getFile({ userId: userData.id, fileId: file.id });
        onOpenChange(false);
      } else {
        toast({
          variant: "error",
          title: t(
            "visit.sidebar.multiRecording.messages.userFileFailed.title"
          ),
          description: t(
            "visit.sidebar.multiRecording.messages.userFileFailed.description"
          ),
        });
      }
    } catch {
      toast({
        variant: "error",
        title: t("visit.sidebar.multiRecording.messages.requestFailed.title"),
        description: t(
          "visit.sidebar.multiRecording.messages.requestFailed.description"
        ),
      });
    } finally {
      setSubmitting(false);
    }
  };

  const resetStates = (): void => {
    setShowRecorder(false);
    setIsFileUpload(false);
    setAudioFinished(false);
    setRecordedBlobUrl(null);
    setSubmitting(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  useEffect(() => {
    if (!open) {
      resetStates();
      resetError();
    }
  }, [open, resetError]);

  return (
    <Sheet
      open={open}
      onOpenChange={(isOpen) => {
        if (!loadingMultiRecordingFile) {
          onOpenChange(isOpen);
        }
      }}
    >
      <SheetContent
        className="w-full sm:max-w-md p-0 sm:rounded-l-2xl"
        onEscapeKeyDown={(event) => {
          event.preventDefault();
        }}
        onInteractOutside={(event) => {
          event.preventDefault();
        }}
      >
        <SheetHeader />
        <input
          ref={fileInputRef}
          accept=".m4a, .mp3, .mp4, .mpeg, .mpga, .ogg, .oga, .wav, .webm"
          style={{ display: "none" }}
          type="file"
          onChange={handleFileChange}
        />
        <div className="flex flex-col flex-1 h-full">
          <div className="flex-1 flex flex-col items-center justify-center gap-8 py-6 px-10">
            {isFileUpload ? (
              recordedBlobUrl && (
                <RecorderAudioPlayer mediaBlobUrl={recordedBlobUrl} />
              )
            ) : showRecorder ? (
              <AudioRecorderUI
                onFinished={setAudioFinished}
                onRecordingComplete={handleRecordingComplete}
              />
            ) : (
              <>
                <MicrophoneIcon
                  aria-label="Mic"
                  height={100}
                  strokeWidth="0.5"
                  width={100}
                />
                <Button
                  className="w-full"
                  size={"xl"}
                  onClick={() => {
                    setShowRecorder(true);
                  }}
                >
                  {t("visits.sidebar.recordAudio")}
                </Button>
                <UploadAudioButton
                  onClick={() => fileInputRef.current?.click()}
                />
                {error && (
                  <AlertMessage
                    description={error}
                    title={t("error.formRecorder.titleMessage")}
                    variant="destructive"
                  />
                )}
              </>
            )}
          </div>
          <SheetFooter className="bg-tinted-grey-1 rounded-bl-2xl flex flex-col sm:space-x-0 sm:flex-col sm:justify-center content-center px-10 py-6 gap-2">
            {audioFinished && (
              <SubscriptionOnlyButton
                className="w-full"
                disabled={loadingMultiRecordingFile || submitting}
                size={"xl"}
                validations={[
                  ValidationSuscriptionButton.PLAN_FREE_TRIAL,
                  ValidationSuscriptionButton.RECORDING_USAGES,
                ]}
                onClick={handleSubmit}
              >
                {loadingMultiRecordingFile || submitting
                  ? `${t("visit.sidebar.multiRecording.procesing")}...`
                  : t("visit.sidebar.multiRecording.submit")}
              </SubscriptionOnlyButton>
            )}
            <ConfirmDialog
              description={t("visits.sidebar.cancelConfirmation.description")}
              open={showCancelConfirmation}
              title={t("visits.sidebar.cancelConfirmation.title")}
              trigger={
                <Button
                  className="w-full"
                  disabled={loadingMultiRecordingFile || submitting}
                  size={"xl"}
                  variant={"grey"}
                  onClick={() => {
                    setShowCancelConfirmation(true);
                  }}
                >
                  {t("visits.sidebar.cancel")}
                </Button>
              }
              onOpenChange={setShowCancelConfirmation}
              onConfirm={() => {
                if (!loadingMultiRecordingFile && !submitting) {
                  resetStates();
                  onOpenChange(false);
                }
              }}
            />
          </SheetFooter>
        </div>
      </SheetContent>
    </Sheet>
  );
}
